import type { ExperienceSequence, ExperienceSearchFacets } from "@/lib/types";
import { CATEGORY_META } from "@/lib/experiences/categoryMeta";
import { ItineraryMediaGallery } from "@/components/experiences/itinerary/ItineraryMediaGallery";
import { getMarketplaceSource, getMarketplaceCounts } from "./marketplaceInventory";
import { isShowLocalReferencesEnabled } from "@/lib/experiences/launchFlags";
import { deriveResultsHeroSummary } from "./resultsHeroSummary";

interface ResultsHeroProps {
  sequence:  ExperienceSequence;
  adults?:   number;
  children?: number;
  selectedIds?:      Set<string>;
  hasSelection?:     boolean;
  onResetSelection?: () => void;
  /**
   * Provider-Only Marketplace Hotfix V1 — Part A. Without these, ResultsHero
   * only ever knew about /experiences/sequence's own (tier-capped, shallow)
   * marketplace_inventory/provider_count — which is exactly what produced
   * the Madrid bug ("3 experiencias"/"3 con proveedor" from the shallow
   * sequence pool, never updated once /experiences/search loaded 17 real
   * providers). All optional/undefined for multi-city sequences, which
   * never call /experiences/search at all — the sequence-derived counts
   * remain correct and unchanged for that case. See resultsHeroSummary.ts
   * for the actual priority logic (unit-tested directly there).
   */
  isMultiCity?:            boolean;
  searchReady?:            boolean;
  searchUsingFallback?:    boolean;
  searchDestinationDepth?: { provider_count: number; local_count: number } | null;
  searchFacets?:           ExperienceSearchFacets | null;
}

// ─── Results hero ───────────────────────────────────────────────────────────────
// Compact editorial header: one real-photography banner (reused from the old
// itinerary gallery, unchanged) plus a single slim facts line underneath — no
// boxed "dashboard card", no duplicated desktop/mobile blocks. Replaces
// ItineraryIntro, whose xl+ variant used a small dashboard-style thumbnail row
// instead of real imagery.
export function ResultsHero({
  sequence, adults, children,
  selectedIds, hasSelection = false, onResetSelection,
  isMultiCity = false, searchReady = false, searchUsingFallback = false,
  searchDestinationDepth = null, searchFacets = null,
}: ResultsHeroProps) {
  // Frontend Marketplace Integration V1 — reads the backend's marketplace_inventory
  // (deeper, gate-filtered, ranked) when present; falls back to flattening
  // days[].experiences only for older/cached responses. Still the source for
  // the "hasSelection" branch (which items were added to comparison — always
  // the currently-loaded list) and for the pre-search-ready/fallback case.
  const { items: allExps, source } = getMarketplaceSource(sequence);
  const { providerCount: sequenceProviderCount, localCount: sequenceLocalCount } = getMarketplaceCounts(sequence, allExps, source);

  const extraImages = Array.from(new Set(
    allExps
      .map(e => e.image_url)
      .filter((u): u is string => typeof u === "string" && u.startsWith("https://"))
  )).slice(0, 2);

  const destinationLabel = sequence.city_display || sequence.city;
  const selectedExps = hasSelection && selectedIds ? allExps.filter(exp => selectedIds.has(exp.id)) : null;

  const { headline, countsLine, explanationLine } = deriveResultsHeroSummary({
    totalActivities:       allExps.length,
    sequenceProviderCount,
    sequenceLocalCount,
    destinationLabel,
    hasSelection,
    selectedCount:         selectedExps?.length,
    selectedProviderCount: selectedExps?.filter(e => e.source_type === "provider").length,
    selectedLocalCount:    selectedExps ? selectedExps.length - selectedExps.filter(e => e.source_type === "provider").length : undefined,
    isMultiCity,
    searchReady,
    searchUsingFallback,
    searchDestinationDepth,
    searchFacets:          searchFacets ? { total_provider_count: searchFacets.total_provider_count, total_local_count: searchFacets.total_local_count } : null,
    showLocalReferences:   isShowLocalReferencesEnabled(),
  });

  return (
    <div className="mb-4">
      <ItineraryMediaGallery sequence={sequence} extraImages={extraImages} />

      <div className="mt-3 flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          {sequence.interests.slice(0, 4).map(interest => {
            const meta = CATEGORY_META[interest];
            return (
              <span
                key={interest}
                className="text-[11px] font-semibold"
                style={{ color: meta?.color ?? "oklch(0.50 0.08 200)" }}
              >
                {meta?.label ?? interest}
              </span>
            );
          })}
          {adults != null && adults > 0 && (
            <span className="text-[11px]" style={{ color: "oklch(0.545 0.020 60 / 0.50)" }}>
              {sequence.interests.length > 0 ? "· " : ""}{adults} {adults === 1 ? "adulto" : "adultos"}
              {children != null && children > 0 && ` · ${children} ${children === 1 ? "niño" : "niños"}`}
            </span>
          )}
        </div>

        {/* Line 1 — prominent: destination + total inventory. Readable size/contrast
            on mobile, where this is the only count the user sees at a glance. */}
        <p className="text-[14px] sm:text-[13px] font-semibold sm:font-normal" style={{ color: "oklch(0.30 0.015 55)" }}>
          {headline}
        </p>

        {/* Line 2 — compact provider/local breakdown */}
        {countsLine && (
          <p className="text-[12px] sm:text-[13px]" style={{ color: "oklch(0.42 0.015 55 / 0.85)" }}>
            {countsLine}
          </p>
        )}

        {/* Supporting marketplace explanation — redundant with the two lines
            above once you already know the counts, so it's desktop-only. */}
        {explanationLine && (
          <p className="hidden sm:block text-[13px] leading-relaxed" style={{ color: "oklch(0.38 0.010 55 / 0.68)" }}>
            {explanationLine}
          </p>
        )}

        {hasSelection && (
          <button
            type="button"
            onClick={onResetSelection}
            disabled={!onResetSelection}
            className="self-start text-[10px] font-semibold transition-opacity hover:opacity-70 disabled:cursor-default"
            style={{ color: "#5A5A5A", background: "none", border: "none", padding: 0 }}
          >
            Vaciar comparación
          </button>
        )}
      </div>
    </div>
  );
}
